import { subDays } from 'date-fns';
import type { IdentitySchema } from '$api-clients/generated';
import { Scopes } from '$authentication/useHasPermission/constants';
import { useHasPermission } from '$authentication/useHasPermission/useHasPermission';

type TeamMemberRole = IdentitySchema.components['schemas']['TeamMemberRole'];

interface UseAgentDslFiltersOptions {
  roles?: TeamMemberRole[];
  licenseStates?: string[];
}

function toDslInClause(field: string, values: string[]) {
  return `${field}[in]=[${values.join(',')}]`;
}

/**
 * Standard agent filters for identity `q` params (`/team-members` and the
 * interaction-permissions team-members endpoints).
 */
export function useAgentDslFilters({
  roles = ['AGENT'],
  licenseStates = [],
}: UseAgentDslFiltersOptions = {}): string[] {
  const filters = [
    toDslInClause('roles', roles),
    // Creates a ISO formatted date for 23:59:59:000 yesterday. If the license
    // expires any time today or after, the member will be included. We don't
    // want to use the current time because otherwise identical requests would
    // have different dates and invalidate the cache.
    `license.expiration[gte]=${subDays(
      new Date(new Date().setHours(23, 59, 59, 0)),
      1,
    ).toISOString()}`,
  ];

  if (licenseStates.length) {
    filters.push(toDslInClause('license.state', licenseStates));
  }

  const { hasPermission: hasAccessToInactiveUsers } = useHasPermission({
    roles: ['ADMIN'],
    scopes: [Scopes.INTERACTIONS_WRITE],
  });
  if (!hasAccessToInactiveUsers) {
    filters.push(
      toDslInClause('status', ['ACTIVE', 'ONBOARDING', 'OFFBOARDING']),
      `user.status[eq]=ACTIVE`,
    );
  }

  return filters;
}
